import CanvasEditorComponent from '@/Components/CanvasEditorComponent';
import { AlertDialog } from '@/Components/ui/AlertDialog';
import BrushIcon from '@mui/icons-material/Brush';
import CloseIcon from '@mui/icons-material/Close';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { Box, Button, IconButton, Modal, Skeleton, Stack, Tooltip, Typography } from '@mui/material';
import type React from 'react';
import { Suspense, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLazyLoadQuery } from 'react-relay';
import FileUploader from './FileUploader';
import type { ModalBodyProps } from './ModalButton';
import SearchBar from './SearchBar';

import { getMediaItemUrl } from '@/Components/posts/utils';
import { useUpdateSearchParam } from '@/Utility/hooks';
import { ImageMediaPlaceHolder } from '@/assets/images';
import { deleteZnglyVideoMutation } from '@/graphql/mutations/ZnglyVideo.mutation';
import {
  MediaQuery,
  MediaQueryPaginationFragment,
  type Media_Query,
  type Media_Query_Pagination_Fragment$data,
  type Media_Query_Pagination_Fragment$key,
} from '@/graphql/queries/Media.query';
import { usePaginationFragment } from '@/graphql/relay.utils';
import { MediaItemSizeEnum, type MimeTypeEnum } from '@/graphql/types';
import { useSearch } from '@tanstack/react-router';
import { toast } from 'react-toastify';

import './MediaPicker.less';

type ModalProps = {
  buttonTitle?: string;
  buttonIcon?: React.ReactElement;
  buttonStyle?: React.CSSProperties;
  buttonClassName?: string;
  showThumbnailButton?: boolean;
};

type Props = {
  disabled?: boolean;
  onFileSelect: (file: MediaItemNode | null) => void;
  mimeTypes: MimeTypeEnum[];
  filterByCanvasCreated?: boolean;
};

type DataProps = {
  queryRef: Media_Query_Pagination_Fragment$key;
};

export type MediaItemNode = NonNullable<Media_Query_Pagination_Fragment$data['mediaItems']>['edges'][number]['node'];

type MediaItemProps = {
  node: MediaItemNode;
  selected: boolean;
  onClick: (node: MediaItemNode | null) => void;
};

const MediaItem = memo(({ node, selected, onClick }: MediaItemProps) => {
  const [active, setActive] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const { mediaItemImage, mediaType } = useMemo(() => {
    let mediaItemImage = getMediaItemUrl(node, MediaItemSizeEnum.Large) ?? '';
    const mediaType = node?.mimeType?.split?.('/')?.[1];

    if (!node?.mimeType?.includes?.('image')) mediaItemImage = ImageMediaPlaceHolder;

    return { mediaItemImage, mediaType };
  }, [node]);

  const handleClick = () => {
    const newState = !active;

    if (newState) onClick(node);
    else onClick(null);
  };

  useEffect(() => {
    if (selected) setActive(true);
    else setActive(false);
  }, [selected]);

  return (
    <Box
      key={node.id}
      sx={{ p: 1, mb: 1, border: selected ? '2px solid #06bcff' : '2px solid transparent', cursor: 'pointer' }}
      className="MediaItem"
    >
      <Box className="MediaItemImg" onClick={handleClick}>
        <p>{mediaType}</p>
        <img src={mediaItemImage} />
      </Box>
      <Stack className="MediaItemInfo" justifyContent={'space-between'} direction={'row'} onClick={handleClick}>
        <Tooltip title={node?.title}>
          <p>
            <b>{node?.title}</b>
          </p>
        </Tooltip>
        <AlertDialog
          title={`Delete ${node?.title}?`}
          description={`Are you sure you want to delete ${node?.title}? This Action is not reversable.`}
          buttonElement={
            <Button className="z-btn-1 red">
              <DeleteForeverIcon />
            </Button>
          }
          cancelText="Cancel"
          acceptButton={({ closeDialog }) => (
            <Button
              className="z-btn-1 red"
              disabled={isDeleting}
              onClick={(e) => {
                e.preventDefault();
                if (isDeleting) return;
                setIsDeleting(true);

                deleteZnglyVideoMutation(
                  {
                    input: {
                      id: node.id,
                      deleteFile: true,
                      deleteImage: true,
                      forceDelete: true,
                    },
                  },
                  {
                    onCompleted: (_, errors) => {
                      closeDialog();
                      setIsDeleting(false);
                      if (!!errors?.length) {
                        toast.error(`Could not delete: ${errors.map((e) => e.message).join(', ')}`);
                        return;
                      }
                      toast.success(`Successfully deleted ${node.title}`);
                    },
                  }
                );
              }}
            >
              Delete
            </Button>
          )}
        />
      </Stack>
    </Box>
  );
});

/**
 * Determines if a media item was created using the Canvas Editor.
 * Checks if the description contains either the ZNGLY_CANVAS_EDITOR prefix
 * or the isZnglyCanvas:true JSON property which are automatically added
 * to items created with the canvas thumbnail tool.
 */

const isCanvasCreatedItem = (mediaItem: MediaItemNode): boolean => {
  return (
    typeof mediaItem?.description === 'string' &&
    (mediaItem.description.startsWith('ZNGLY_CANVAS_EDITOR:') || mediaItem.description.includes('"isZnglyCanvas":true'))
  );
};

const MediaPickerContent = ({
  onFileSelect,
  queryRef,
  closeModal,
  mimeTypes,
  modalRef,
  disabled,
  filterByCanvasCreated = false,
}: Props & DataProps & ModalBodyProps) => {
  const [selectedNode, setSelectedNode] = useState<MediaItemNode | null>(null);
  const updateSearchParam = useUpdateSearchParam();

  const searchTerm = useSearch({
    strict: false,
    select(state) {
      return state?.search;
    },
  });

  const { data, hasNext, isLoadingNext, loadNext, refetch } = usePaginationFragment<
    Media_Query,
    Media_Query_Pagination_Fragment$key
  >(MediaQueryPaginationFragment, queryRef);

  useEffect(() => {
    refetch(
      {
        first: 25,
        where: {
          search: searchTerm || null,
          mimeTypes,
        },
      },
      { fetchPolicy: 'store-or-network' }
    );
  }, [searchTerm, refetch, mimeTypes]);

  useEffect(() => {
    return () => {
      updateSearchParam('search', undefined);
    };
  }, []);

  const onMediaItemClick = useCallback((node: MediaItemNode | null) => {
    setSelectedNode(node);
  }, []);

  const items = useMemo(
    () => (data?.mediaItems?.edges || []).filter(({ node }) => !!node).map(({ node }) => node),
    [data?.mediaItems?.edges]
  );

  const displayItems = useMemo(() => {
    let filteredItems = (data?.mediaItems?.edges || []).filter(({ node }) => !!node).map(({ node }) => node);

    if (filterByCanvasCreated) {
      filteredItems = filteredItems.filter(isCanvasCreatedItem);
    }

    return filteredItems;
  }, [data?.mediaItems?.edges, filterByCanvasCreated]);

  const handleScroll = useCallback(() => {
    if (!hasNext || isLoadingNext) return;
    if (!modalRef.current) return;

    const currPos = modalRef?.current?.scrollTop + modalRef?.current?.clientHeight;
    const maxPos = modalRef.current?.scrollHeight;
    const threshold = 0.85;

    if (currPos / maxPos > threshold) loadNext(30);
  }, [loadNext, hasNext, isLoadingNext, modalRef]);

  useEffect(() => {
    if (modalRef.current) modalRef.current.addEventListener('scroll', handleScroll);
    return () => {
      if (modalRef.current) modalRef.current.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  const acceptType = useMemo(() => {
    if (mimeTypes.every((type) => type.toLowerCase().indexOf('font_') > -1)) return 'font/*';
    if (mimeTypes.every((type) => type.toLowerCase().indexOf('audio_') > -1)) return 'audio/*';
    if (mimeTypes.every((type) => type.toLowerCase().indexOf('video_') > -1)) return 'video/*';
    if (mimeTypes.every((type) => type.toLowerCase().indexOf('image_') > -1)) return 'image/*';

    return '';
  }, [mimeTypes]);

  return (
    <Box className="MediaPicker">
      <Box className="MediaPickerTopBar">
        <FileUploader
          disabled={disabled}
          acceptType={acceptType}
          onFileSelect={() => {}}
          connections={data?.mediaItems?.__id ? [data.mediaItems.__id] : []}
          mimeTypes={mimeTypes}
        />
        <Typography className="MediaPickerSubtitle">Upload a file or select from the media library below</Typography>
        <SearchBar />
      </Box>

      <Box className="MediaPickerBody">
        {displayItems.map((node) => (
          <MediaItem key={node.id} node={node} selected={node.id === selectedNode?.id} onClick={onMediaItemClick} />
        ))}
      </Box>

      <Box className="MediaPickerOptionsBar">
        <Button
          className="z-btn-5"
          disabled={!selectedNode || disabled}
          onClick={() => {
            onFileSelect(selectedNode);
            updateSearchParam('search', undefined);
            closeModal();
          }}
        >
          Select
        </Button>
      </Box>
    </Box>
  );
};

const MediaPicker = (props: Props & DataProps & ModalBodyProps) => {
  return <MediaPickerContent {...props} />;
};

const MediaPickerFallback = () => {
  return (
    <Box className="MediaPicker">
      <Box className="MediaPickerTopBar">
        <FileUploader acceptType="" onFileSelect={() => {}} />
        <Typography className="MediaPickerSubtitle">And/Or Select from the Media Library below</Typography>
        <SearchBar />
      </Box>

      <Box sx={{ mt: 1, mb: 3, ml: 2, mr: 2 }}>
        <Skeleton height={38} />
        <Skeleton height={38} />
        <Skeleton height={38} />
      </Box>

      <Box className="MediaPickerOptionsBar">
        <Button className="z-btn-5">Select</Button>
      </Box>
    </Box>
  );
};

const MediaPickerLoad = (props: Props & ModalBodyProps) => {
  const data = useLazyLoadQuery<Media_Query>(MediaQuery, {
    first: 25,
    where: {
      search: null,
      mimeTypes: props.mimeTypes,
    },
  });

  return <MediaPicker {...props} queryRef={data} />;
};

const MediaPickerModalButton = (props: Props & ModalProps) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [showCanvasEditor, setShowCanvasEditor] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  const updateSearchParam = useUpdateSearchParam();

  const closeModal = useCallback(() => {
    updateSearchParam('search', undefined);
    setModalOpen(false);
  }, [updateSearchParam]);

  return (
    <>
      <Button
        className={`z-btn-5 ${props?.buttonClassName}`}
        startIcon={props.buttonIcon}
        style={props?.buttonStyle}
        disabled={props?.disabled}
        onClick={() => {
          setModalOpen(true);
        }}
      >
        {props.buttonTitle ?? 'Choose File'}
      </Button>

      {props.showThumbnailButton !== false && (
        <Button
          className="z-btn-5"
          startIcon={<BrushIcon />}
          disabled={props?.disabled}
          onClick={() => setShowCanvasEditor(true)}
        >
          Create Thumbnail
        </Button>
      )}

      <Modal
        open={modalOpen}
        onClose={closeModal}
        className="mediaPicker"
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        {modalOpen ? (
          <Box className={'ZnglyModal Big'} sx={{ p: 0 }} ref={modalRef}>
            <IconButton className="ZnglyModal-close-button" aria-label="close" size="large" onClick={closeModal}>
              <CloseIcon fontSize="inherit" />
            </IconButton>
            <Suspense fallback={<MediaPickerFallback />}>
              <MediaPickerLoad {...props} closeModal={closeModal} modalRef={modalRef} />
            </Suspense>
          </Box>
        ) : (
          <></>
        )}
      </Modal>
      {showCanvasEditor && <CanvasEditorComponent onClose={() => setShowCanvasEditor(false)} />}
    </>
  );
};

export type MediaPickerNode = MediaItemNode;

export default MediaPickerModalButton;
